/* eslint-disable @typescript-eslint/camelcase */
import { Grid, makeStyles } from '@material-ui/core';
import moment from 'moment';
import Button from 'components/Button';
import i18n from 'i18n';
import {
  getAccessControlRequest,
  setAccessControlRequest,
} from 'modules/clients/actions';
import {
  AccessControlProps,
  BlockedPeriodsProps,
  SettingsAccessControlProps,
} from 'modules/clients/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import ApplicationState from 'store/interfaces';
import { CompanyData, RolesEnum } from 'modules/authenticate/interfaces';
import ClientsTemplate from '../Templates/Clients';
import FormAllowedIps from './FormAllowedIps';
import FormBlockedPeriods from './FormBlockedPeriods';
import styles from './styles';

const useStyles = makeStyles(styles);

interface DataProps {
  settings: SettingsAccessControlProps;
  allowedIps: Array<{ ip_address: string }>;
  blockedPeriods: BlockedPeriodsProps[];
}

interface Props {
  accessControl: AccessControlProps;
  role?: RolesEnum;
  companySelected: CompanyData | null;
  getAccessControlRequest: ({ id }) => Promise<void>;
  setAccessControlRequest: ({ id, data }) => Promise<void>;
}

const AccessRestrictions = ({
  accessControl,
  role,
  companySelected,
  getAccessControlRequest,
  setAccessControlRequest,
}: Props) => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [data, setData] = React.useState<DataProps>({
    allowedIps: [],
    blockedPeriods: [],
    settings: {
      control_by_schedule: false,
      control_by_ip: false,
    },
  });
  const [hasError, setHasError] = React.useState(false);

  // eslint-disable-next-line consistent-return
  const handleSubmit = () => {
    setAccessControlRequest({ id, data });
  };

  const filterBlockedPeriodsData = (data: BlockedPeriodsProps[]) => {
    return data.map(item => {
      return {
        ...item,
        start_restriction_at: moment(item?.start_restriction_at).format(
          'HH:mm:ss'
        ),
        stop_restriction_at: moment(item?.stop_restriction_at).format(
          'HH:mm:ss'
        ),
      };
    });
  };

  React.useEffect(() => {
    setData({
      ...data,
      settings: accessControl?.settings,
      allowedIps: accessControl?.allowedIps.results,
      blockedPeriods: accessControl?.blockedPeriods.results,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessControl, id]);

  React.useEffect(() => {
    getAccessControlRequest({ id });
  }, [getAccessControlRequest, id]);

  return (
    <ClientsTemplate
      current="access-restrictions"
      title={i18n.t('CLIENTS_PAGE.RESTRICTIONS_TITLE_KEY')}
      subtitle={i18n.t('CLIENTS_PAGE.RESTRICTIONS_SUBTITLE_KEY')}
      breadcrumbs={[
        {
          name: i18n.t('CLIENT_KEY'),
          link: '/clients',
        },
        {
          name: i18n.t('CLIENTS_PAGE.RESTRICTIONS_SUBTITLE_KEY'),
        },
      ]}
    >
      <Grid container spacing={4}>
        <Grid item className={classes.content} xs={6}>
          <FormBlockedPeriods
            role={role}
            companySelected={companySelected}
            active={accessControl?.settings.control_by_schedule}
            onChange={(values, control_by_schedule) => {
              setData({
                ...data,
                settings: { ...data.settings, control_by_schedule },
                blockedPeriods: filterBlockedPeriodsData(values),
              });
            }}
          />
        </Grid>

        <Grid item className={classes.content} xs={6}>
          <FormAllowedIps
            role={role}
            companySelected={companySelected}
            active={accessControl?.settings.control_by_ip}
            onError={setHasError}
            onChange={(values, control_by_ip) =>
              setData({
                ...data,
                settings: { ...data.settings, control_by_ip },
                allowedIps: values.map(ip_address => ({ ip_address })),
              })
            }
          />
        </Grid>
      </Grid>

      <Grid container justify="flex-end" className={classes.buttons}>
        <Button
          id="submit-button"
          disabled={hasError}
          onClick={handleSubmit}
          submitButton
        >
          {i18n.t('SAVE_KEY')}
        </Button>
        <Button
          id="cancel-button"
          variant="outlined"
          onClick={() => history.push('/environments')}
        >
          {i18n.t('CANCEL_KEY')}
        </Button>
      </Grid>
    </ClientsTemplate>
  );
};

const mapStateToProps = ({
  clients: { accessControl },
  authenticate: { companySelected, data },
}: ApplicationState) => ({
  accessControl,
  companySelected,
  role: data?.role,
});

export default connect(mapStateToProps, {
  getAccessControlRequest,
  setAccessControlRequest,
})(AccessRestrictions);
